import { Component, OnInit, OnDestroy, AfterViewInit, Input, Output, EventEmitter, OnChanges } from '@angular/core';
import { debounceTime, distinctUntilChanged, finalize, map, Subscription } from 'rxjs';
import { FormGroup, FormControl, Validators, FormBuilder } from '@angular/forms';
import { IPlace, IProiect } from '../../proiecte.interface';
import { MapsService } from 'src/app/services/maps.service';
import { helper } from 'src/environments/helper';
import { PermissionsService } from 'src/app/services/permissions.service';
import { ProiecteService } from '../../proiecte.service';
import { NotificationService } from 'src/app/services/notification.service';
import { IUpdateAdresaForm, IUpdateAdresaPayload } from './locatie-proiect.interface';

@Component({
    selector: 'app-locatie-proiect',
    templateUrl: './locatie-proiect.component.html',
    styleUrls: ['./locatie-proiect.component.scss']
})
export class LocatieProiectComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
    @Input() proiect!: IProiect;
    @Output() proiectChanged: EventEmitter<void> = new EventEmitter();

    showEditField = false;
    searchedPlaces: IPlace[] = [];
    passedProiectId!: number;
    placeData!: any;
    isLoading = false;

    helper = helper;
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;
    successTitle: string = helper.dialogConfig.headers.success;
    successIcon: string = helper.dialogConfig.icons.success;
    successType: string = helper.dialogConfig.icons.success;

    defaultMapCoords = { lat: 44.4277119, lng: 26.0994927 }

    draggableMarker: any;
    infoWindow: any;
    addressNameSubscription: Subscription = new Subscription();

    form!: FormGroup;

    constructor(
        private mapsService: MapsService,
        private proiecteService: ProiecteService,
        private notificationService: NotificationService,
        private fb: FormBuilder,
        public permissionsService: PermissionsService,
    ) { }

    ngOnInit(): void {
        this.form = this.fb.group({
            addressName: new FormControl('', [Validators.required]),
            lat: new FormControl('', [Validators.required]),
            lng: new FormControl('', [Validators.required]),
            addressObs: new FormControl(this.proiect?.locatie_obs || '', []),
        });
    }

    ngOnChanges(): void {
        this.passedProiectId = this.proiect.id_proiect;
    }

    ngOnDestroy(): void {
        this.toggleAddressNameSubscription(false);
    }

    async ngAfterViewInit() {
        await this.initMap();
        this.toggleAddressNameSubscription(true);
    }

    async initMap() {
        // librariile necesare pentru randare harta si creare pin draggable.
        const { Map, InfoWindow } = await google.maps.importLibrary("maps") as google.maps.MapsLibrary;
        const { AdvancedMarkerElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;

        const map = new Map(document.getElementById('map') as HTMLElement, {
            center: this.defaultMapCoords,
            zoom: 14,
            mapId: '4504f8b37365c3d1',
        });

        this.infoWindow = new InfoWindow();

        this.draggableMarker = new AdvancedMarkerElement({
            map,
            position: this.defaultMapCoords,
            gmpDraggable: false,
            title: "Centrati pin-ul pe locatia proiectului. Click pe harta sau trageti de pin.",
        });
        this.draggableMarker.addListener('dragend', (event: any) => {
            const position = this.draggableMarker.position as google.maps.LatLng;
            this.reverseGeodecoding(position);
        });

        const position = { lat: +this.proiect.lat, lng: +this.proiect.lng };
        this.repositionPin(position);
        this.reverseGeodecoding(position);
    }

    repositionPin(position: any, address: any = null) {
        this.draggableMarker.position = position;
        this.draggableMarker.map.panTo(position); // centreaza harta cu animatie
        this.form.controls['lat'].setValue(position.lat);
        this.form.controls['lng'].setValue(position.lng);
        this.draggableMarker.title = `Pin setat la: ${position.lat}, ${position.lng}.`;;
        //this.showMarkerInfo(position); // afiseaza un popup pe marker cu coordonatele
    }

    reverseGeodecoding(position: any) {
        let geocoder = new google.maps.Geocoder();
        let latlng = new google.maps.LatLng(position.lat, position.lng);

        let request = {
            location: latlng
        };

        geocoder.geocode(request, (results, status) => {
            if (status == google.maps.GeocoderStatus.OK) {
                if (results && results[0] != null) {
                    const placeId = results[0].place_id
                    const address = results[0].formatted_address;
                    this.searchedPlaces.push({ id: placeId, locatie: address })
                    this.toggleAddressNameSubscription(false);
                    this.form.controls['addressName'].setValue(address);
                    this.form.controls['lat'].setValue(position.lat);
                    this.form.controls['lng'].setValue(position.lng);
                    this.draggableMarker.title = `Pin setat la: ${position.lat}, ${position.lng}.`;
                    this.toggleAddressNameSubscription(true);
                } else {
                    this.showMarkerInfo(position, "Adresa necunoscută.");
                }
            }
            else {
                console.warn('reverse geodecoding status : ', status);
                this.showMarkerInfo(position, "Adresa nu a putut fi decodată pe baza coordonatelor alese de pe hartă.");
            }
        })
    }

    showMarkerInfo(position: any, content: null | string = null) {
        this.infoWindow.close();
        this.infoWindow.setContent(content ?? `Pin setat la: ${position.lat}, ${position.lng}`);
        this.infoWindow.open(this.draggableMarker.map, this.draggableMarker);
    }

    onPlaceSelected(option: { id: string; locatie: string }) {
        this.isLoading = true;
        this.mapsService.getPlaceData(option.id)
            .pipe(
                map((results: any) => results.data),
                finalize(() => (
                    this.isLoading = false
                ))
            ).subscribe({
                next: (results: any) => {
                    this.placeData = results;
                    this.repositionPin(this.placeData.geometry.location)
                },
                error: (error: any) => {
                    console.error('Error loading the place data', error);
                },
            });
    }

    toggleAddressNameSubscription(subscribe: boolean) {
        if (subscribe) {
            this.addressNameSubscription = this.form.controls['addressName'].valueChanges
                .pipe(
                    debounceTime(1000),
                    distinctUntilChanged()
                )
                .subscribe((searchTerm: string) => {
                    this.isLoading = true;
                    this.mapsService.findPlaces({ input: searchTerm })
                        .subscribe((response: any) => {
                            this.isLoading = false;
                            this.searchedPlaces = response.data;
                        });
                });
        }
        else {
            if (this.addressNameSubscription) {
                this.addressNameSubscription.unsubscribe();
            }
        }
    }

    get addressName() {
        return this.form.controls['addressName'].value;
    }

    submitForm() {
        this.isLoading = true;

        const {
            addressName,
            lng,
            lat,
            addressObs
        } = this.form.value as IUpdateAdresaForm;
        const payload: IUpdateAdresaPayload = {
            address: addressName,
            lng,
            lat,
            locatie_obs: addressObs,
        };

        this.proiecteService
            .updateAdresa(this.passedProiectId, payload)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.notificationService.warningSwal(
                        this.successTitle,
                        'Proiect actualizat cu succes!',
                        this.successIcon
                    ).then(() => {
                        this.proiectChanged.emit();
                        this.closeEditField();
                    });
                },
                error: (err: any) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    toggleEditField() {
        this.showEditField = !this.showEditField;
        this.draggableMarker.gmpDraggable = this.showEditField;
    }

    closeEditField() {
        this.draggableMarker.gmpDraggable = false;
        this.showEditField = false;
    }
}
